import {
  AnchorButton,
  Classes,
  Collapse,
  Divider,
  H5,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import {
  DataSourceTable,
  DataSourceTableSelectItem,
} from 'src/constants/dataSourceTable';
import { TEST_IDS } from 'src/constants/testIds';
import { QueryStatus } from 'src/constants/virtualGraphs';
import { DataSourcePanelContainer } from 'src/containers/ProjectResourceDialog/DataSourcePanelContainer';
import {
  ProjectVirtualGraphToCreate,
  ProjectVirtualGraphsToCreate,
} from 'src/containers/ProjectResourceDialog/ProjectVirtualGraphCreationPanel/types';
import { ProjectVirtualGraphPanelExistingScopeContainer } from 'src/containers/ProjectVirtualGraphPanel/ProjectVirtualGraphPanelExistingScopeContainer';
import { ProjectVirtualGraphPanelNewScopeContainer } from 'src/containers/ProjectVirtualGraphPanel/ProjectVirtualGraphPanelNewScopeContainer';
import { ProjectVirtualGraphPanelProperties } from 'src/containers/ProjectVirtualGraphPanel/ProjectVirtualGraphPanelProperties';
import { ProjectVirtualGraphPreview } from 'src/containers/ProjectVirtualGraphPanel/ProjectVirtualGraphPreview';
import { getCustomQueryFromVgsToCreate } from 'src/containers/ProjectVirtualGraphPanel/utils/getCustomQueryFromVgsToCreate';
import { getTablesFromVgsToCreate } from 'src/containers/ProjectVirtualGraphPanel/utils/getTablesFromVgsToCreate';
import { connectionSelector } from 'src/selectors/connection';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { DataSource } from 'src/store/state/dataSources';
import {
  ProjectVirtualGraph,
  ProjectVirtualGraphField,
  ProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { generateSqlQueryForTable } from 'src/utils/generateSqlQueryForTable';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { usePrevious } from 'src/utils/hooks/usePrevious';
import { useProjectDataSourceQuery } from 'src/utils/hooks/useProjectDataSourceQuery';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

type QueueItem = {
  from: DataSourceTableSelectItem | string;
  virtualGraphId: string;
};

export type ProjectVirtualGraphPanelRefAttributes = {
  abortQuery: () => void;
} | null;

export type ProjectVirtualGraphPanelContents = {
  connectionId?: string;
  fields: ProjectVirtualGraphField[];
  previewRows: string[][];
};

export type ProjectVirtualGraphPanelProps = {
  dataSource: DataSource | null;
  editedVirtualGraph?: ProjectVirtualGraph & {
    errors?: Array<{ message: string }>;
  };
  enablePreviewRefresh?: boolean;
  existingVirtualGraph?: ProjectVirtualGraph;
  handleAddVirtualGraph?: (virtualGraph: ProjectVirtualGraphToCreate) => void;
  handleRemoveVirtualGraph?: (id: string) => void;
  handleUpdateDataSource?: () => void;
  handleUpdateVirtualGraph?: (
    id: string,
    update: Partial<ProjectVirtualGraphToCreate>
  ) => void;
  isPendingTables?: boolean;
  isSettingsOpenKey?: number;
  isUsingCustomQuery: boolean;
  nativeQuoteCharacter: string | null;
  selectedTab: string;
  setErrorText: (errorText: string) => void;
  setIsUsingCustomQuery: (isUsingCustomQuery: boolean) => void;
  setSelectedTab: (selectedTab: string) => void;
  setVirtualGraphOptions: (fileOptions: ProjectVirtualGraphOptions) => void;
  tables: DataSourceTable[];
  updateVirtualGraphOptions: (
    update: Partial<ProjectVirtualGraphOptions>
  ) => void;
  virtualGraphOptions: ProjectVirtualGraphOptions;
  virtualGraphsToCreate: ProjectVirtualGraphsToCreate;
};

export const ProjectVirtualGraphPanel: React.VFC<
  ProjectVirtualGraphPanelProps
> = ({
  dataSource,
  enablePreviewRefresh,
  editedVirtualGraph,
  existingVirtualGraph,
  handleAddVirtualGraph,
  handleRemoveVirtualGraph,
  handleUpdateDataSource,
  handleUpdateVirtualGraph,
  isPendingTables,
  isSettingsOpenKey,
  isUsingCustomQuery,
  nativeQuoteCharacter,
  selectedTab,
  setErrorText,
  setIsUsingCustomQuery,
  setSelectedTab,
  setVirtualGraphOptions,
  tables,
  updateVirtualGraphOptions,
  virtualGraphOptions,
  virtualGraphsToCreate,
}) => {
  const dispatch = useReduxDispatch();

  const currentConnection = useReduxSelector(connectionSelector);
  const isDataSourceQueryOptionsSupported = useReduxSelector(
    isStardogFeatureSupportedSelector(
      'isDataSourceQueryOptionsSupported',
      currentConnection
    )
  );

  const [forceRefresh, setForceRefresh] = React.useState<boolean>(false);
  const [runningQueryQueue, setRunningQueryQueue] = React.useState<
    Array<{
      virtualGraphId: string;
      from: DataSourceTableSelectItem | string;
    }>
  >([]);

  const addItemToQueue = React.useCallback(
    (isExisting = false, ...queueItems: QueueItem[]) => {
      queueItems.forEach(({ from, virtualGraphId }) => {
        if (virtualGraphsToCreate[virtualGraphId]) {
          handleUpdateVirtualGraph?.(virtualGraphId, {
            errors: [],
            from,
          });
        } else {
          let name: string;
          if (isExisting) {
            name = existingVirtualGraph?.name || '';
          } else if (typeof from === 'string') {
            name = `${dataSource?.id || ''} Custom Query`.trimStart();
          } else {
            name = from.name;
          }

          handleAddVirtualGraph?.({
            id: virtualGraphId,
            name,
            from,
            fields: [],
            previewRows: [],
            allResultsIncluded: false,
          });
        }
      });
      setRunningQueryQueue((prevQueue: any[]) => {
        const newRunningQueryQueue = [...prevQueue];
        newRunningQueryQueue.push(...queueItems);
        return newRunningQueryQueue;
      });
    },
    [
      dataSource,
      existingVirtualGraph,
      handleAddVirtualGraph,
      handleUpdateVirtualGraph,
      virtualGraphsToCreate,
    ]
  );

  let currentQueryQueueItem: QueueItem | undefined;
  if (runningQueryQueue?.length) {
    [currentQueryQueueItem] = runningQueryQueue;
  }

  let existingQuery = '';
  if (existingVirtualGraph) {
    existingQuery =
      typeof existingVirtualGraph.from === 'string'
        ? existingVirtualGraph.from
        : generateSqlQueryForTable({
            table: existingVirtualGraph.from,
            nativeQuoteCharacter,
            options: virtualGraphOptions,
            limit: true,
          });
  }

  let vgQuery = '';
  if (currentQueryQueueItem) {
    vgQuery =
      typeof currentQueryQueueItem.from === 'string'
        ? currentQueryQueueItem.from
        : generateSqlQueryForTable({
            table: currentQueryQueueItem.from,
            nativeQuoteCharacter,
            options: virtualGraphOptions,
            limit: true,
          });
  }

  const runningQueryDataSourceId =
    editedVirtualGraph?.dataSourceId || dataSource?.id;
  const runningQueryFields = editedVirtualGraph?.fields;
  const runningQueryOptions = isDataSourceQueryOptionsSupported
    ? virtualGraphOptions
    : undefined;
  const runningQueryArgs = React.useMemo(
    () => ({
      dataSourceId: runningQueryDataSourceId,
      existingFields: runningQueryFields,
      existingQuery,
      forceRefresh,
      query: vgQuery,
      table:
        typeof currentQueryQueueItem?.from === 'object'
          ? currentQueryQueueItem.from
          : undefined,
      virtualGraphOptions: runningQueryOptions,
    }),
    [
      currentQueryQueueItem,
      existingQuery,
      runningQueryDataSourceId,
      runningQueryFields,
      forceRefresh,
      vgQuery,
      runningQueryOptions,
    ]
  );

  const runningQuery = useProjectDataSourceQuery(runningQueryArgs);

  const didIsQueryingChangeVGItem = useDidValueChange(runningQuery.isQuerying);

  // Process items off the queue
  React.useEffect(() => {
    if (currentQueryQueueItem) {
      if (
        currentConnection &&
        didIsQueryingChangeVGItem &&
        !runningQuery.isQuerying
      ) {
        if (runningQueryQueue.length) {
          setRunningQueryQueue((prevQueue) => {
            const newQueue = [...prevQueue];
            newQueue.shift();
            return newQueue;
          });
        }
        if (editedVirtualGraph) {
          if (runningQuery.errors.length > 0) {
            runningQuery.errors.forEach(({ message }) => {
              dispatch(
                projectsActionCreators.updatedProjectVirtualGraphPreviewFailure(
                  message
                )
              );
            });
            handleUpdateVirtualGraph?.(editedVirtualGraph.id, {
              allResultsIncluded: runningQuery.allResultsIncluded,
              errors: runningQuery.errors,
              from: currentQueryQueueItem.from,
              fields: runningQuery.fields,
              previewRows: runningQuery.rows,
            });
          } else if (runningQuery.fields && runningQuery.rows) {
            handleUpdateVirtualGraph?.(editedVirtualGraph.id, {
              allResultsIncluded: runningQuery.allResultsIncluded,
              from: currentQueryQueueItem.from,
              fields: runningQuery.fields,
              previewRows: runningQuery.rows,
            });
          } else if (forceRefresh) {
            dispatch(
              projectsActionCreators.updatedProjectVirtualGraphPreviewFailure(
                copy.components.projectResourceDialog.projectVirtualGraphPanel
                  .preview.errors.emptyRefresh,
                true
              )
            );
          }
        } else {
          handleUpdateVirtualGraph?.(currentQueryQueueItem.virtualGraphId, {
            allResultsIncluded: runningQuery.allResultsIncluded,
            errors: runningQuery.errors,
            fields: runningQuery.fields,
            previewRows: runningQuery.rows,
          });
          if (!selectedTab && setSelectedTab) {
            setSelectedTab(currentQueryQueueItem?.virtualGraphId);
          }
        }
        setForceRefresh(false);
      }
    }
  }, [
    currentConnection,
    currentQueryQueueItem,
    didIsQueryingChangeVGItem,
    dispatch,
    editedVirtualGraph,
    forceRefresh,
    handleAddVirtualGraph,
    handleUpdateVirtualGraph,
    runningQueryQueue,
    runningQuery,
    selectedTab,
    setSelectedTab,
  ]);

  const previousVirtualGraphOptions = usePrevious(virtualGraphOptions);
  const didVirtualGraphOptionsChange = useDidValueChange(virtualGraphOptions);
  const didIsUsingCustomQueryChange = useDidValueChange(isUsingCustomQuery);

  // Re-add tables/queries to the queue when user toggles between modes
  React.useEffect(() => {
    if (
      previousVirtualGraphOptions &&
      (didVirtualGraphOptionsChange ||
        (didIsUsingCustomQueryChange && !isUsingCustomQuery))
    ) {
      addItemToQueue(
        true,
        ...Object.values(virtualGraphsToCreate)
          .filter((virtualGraph) =>
            isUsingCustomQuery
              ? typeof virtualGraph.from === 'string'
              : typeof virtualGraph.from === 'object' &&
                Boolean(virtualGraph.from.value)
          )
          .map<QueueItem>((virtualGraph) => ({
            virtualGraphId: virtualGraph.id,
            from: virtualGraph.from,
          }))
      );
    }
  }, [
    addItemToQueue,
    didVirtualGraphOptionsChange,
    didIsUsingCustomQueryChange,
    isUsingCustomQuery,
    previousVirtualGraphOptions,
    virtualGraphsToCreate,
  ]);

  const { isQuerying } = runningQuery;
  const runningQueryStatus = React.useMemo(() => {
    if (isQuerying) {
      return QueryStatus.QUERYING;
    }
    if (editedVirtualGraph) {
      // Errors are shown as notifications when there are prior previewRows
      // to display
      return QueryStatus.EXISTING_ROWS;
    }
    if (
      !selectedTab ||
      !Object.values(virtualGraphsToCreate).filter((virtualGraph) =>
        isUsingCustomQuery
          ? typeof virtualGraph.from === 'string'
          : typeof virtualGraph.from === 'object'
      ).length
    ) {
      return QueryStatus.NO_QUERY;
    }
    const selectedVirtualGraph = virtualGraphsToCreate[selectedTab || ''];
    if (selectedVirtualGraph?.errors?.length) {
      return QueryStatus.ERROR;
    }
    return QueryStatus.SUCCESS;
  }, [
    isQuerying,
    editedVirtualGraph,
    selectedTab,
    virtualGraphsToCreate,
    isUsingCustomQuery,
  ]);

  const [areResourcePropertiesValid, setAreResourcePropertiesValid] =
    React.useState<boolean>(true);

  const handleRefreshPreview = React.useCallback(async () => {
    runningQuery.abortQuery.current();
    setForceRefresh(true);

    addItemToQueue(
      true,
      ...Object.values(virtualGraphsToCreate)
        .filter((virtualGraph) =>
          isUsingCustomQuery
            ? typeof virtualGraph.from === 'string'
            : typeof virtualGraph.from === 'object' &&
              Boolean(virtualGraph.from.value)
        )
        .map<QueueItem>((virtualGraph) => ({
          virtualGraphId: virtualGraph.id,
          from: virtualGraph.from,
        }))
    );
  }, [
    addItemToQueue,
    isUsingCustomQuery,
    runningQuery.abortQuery,
    virtualGraphsToCreate,
  ]);

  React.useEffect(() => {
    const errors = Object.values(virtualGraphsToCreate).filter(
      (vg) => vg.errors?.length
    );
    const customQueryErrors = errors.filter(
      (vg) => typeof vg.from === 'string'
    );
    const tableErrors = errors.filter((vg) => typeof vg.from === 'object');

    const emptyNames = Object.values(virtualGraphsToCreate).filter(
      (val) => !val.name
    );
    const emptyCustomQueryNames = emptyNames.filter(
      (vg) => typeof vg.from === 'string'
    );
    const emptyTableNames = emptyNames.filter(
      (vg) => typeof vg.from === 'object'
    );
    const customQueryVirtualGraph = getCustomQueryFromVgsToCreate(
      virtualGraphsToCreate
    );
    const tables = getTablesFromVgsToCreate(virtualGraphsToCreate);

    if (isUsingCustomQuery && customQueryErrors.length) {
      setErrorText(
        copy.components.projectResourceDialog.projectVirtualGraphPanel
          .validation.errors
      );
    } else if (!isUsingCustomQuery && tableErrors.length) {
      setErrorText(
        copy.components.projectResourceDialog.projectVirtualGraphPanel.validation.tableErrors(
          tableErrors.map((vg) => {
            const table = vg.from as DataSourceTableSelectItem;
            return table.name;
          })
        )
      );
    } else if (runningQuery.isQuerying) {
      setErrorText(
        copy.components.projectResourceDialog.projectVirtualGraphPanel
          .validation.runningQuery
      );
    } else if (!areResourcePropertiesValid) {
      setErrorText(
        copy.components.projectResourceDialog.projectVirtualGraphPanel
          .validation.invalidResourceProperties
      );
    } else if (
      (isUsingCustomQuery && !customQueryVirtualGraph?.from) ||
      (!isUsingCustomQuery && !tables.length)
    ) {
      setErrorText(
        copy.components.projectResourceDialog.projectVirtualGraphPanel.validation.emptyQuery(
          Boolean(editedVirtualGraph)
        )
      );
    } else if (isUsingCustomQuery && emptyCustomQueryNames.length) {
      setErrorText(
        copy.components.projectResourceDialog.projectVirtualGraphPanel
          .validation.name
      );
    } else if (!isUsingCustomQuery && emptyTableNames.length) {
      setErrorText(
        copy.components.projectResourceDialog.projectVirtualGraphPanel.validation.names(
          emptyTableNames.map((vg) => {
            const table = vg.from as DataSourceTableSelectItem;
            return table.name;
          })
        )
      );
      // error onwards are for editing from preview
    } else if (
      !isUsingCustomQuery &&
      typeof editedVirtualGraph?.from === 'object' &&
      !editedVirtualGraph.from.value
    ) {
      setErrorText(
        copy.components.projectResourceDialog.projectVirtualGraphPanel.validation.emptyQuery(
          true
        )
      );
    } else {
      setErrorText('');
    }
  }, [
    areResourcePropertiesValid,
    editedVirtualGraph,
    isUsingCustomQuery,
    runningQuery,
    setErrorText,
    virtualGraphsToCreate,
  ]);

  const [isSettingsOpen, setIsSettingsOpen] = React.useState(
    Boolean(isSettingsOpenKey)
  );
  React.useEffect(() => {
    setIsSettingsOpen(Boolean(isSettingsOpenKey));
  }, [isSettingsOpenKey]);

  const connectionProperties = React.useMemo(() => {
    if (dataSource) {
      return {
        id: dataSource.id,
        endpoint: dataSource.options?.['jdbc.url'] || '',
        type: dataSource.type,
      };
    }

    if (editedVirtualGraph) {
      return {
        id: editedVirtualGraph.dataSourceId,
        endpoint: editedVirtualGraph.dataSourceEndpoint,
        type: editedVirtualGraph.dataSourceType || undefined,
      };
    }
    return undefined;
  }, [dataSource, editedVirtualGraph]);

  return (
    <div className="sd-project-virtual-graph-panel">
      <DataSourcePanelContainer
        connectionEndpoint={connectionProperties?.endpoint}
        connectionId={connectionProperties?.id}
        connectionType={connectionProperties?.type}
        handleUpdateDataSource={handleUpdateDataSource}
      />
      <div className="sd-project-virtual-graph-panel__scroll-wrapper">
        <div
          className={classNames(
            'sd-project-virtual-graph-panel__settings',
            Classes.CALLOUT
          )}
        >
          <div className="sd-project-virtual-graph-panel__settings__header">
            <AnchorButton
              data-testid={TEST_IDS.projectVirtualGraphPanel.settingsButton}
              icon={
                isSettingsOpen ? IconNames.CHEVRON_DOWN : IconNames.CHEVRON_UP
              }
              minimal
              onClick={() => setIsSettingsOpen(!isSettingsOpen)}
            />
            <H5>Settings</H5>
          </div>

          <Collapse isOpen={isSettingsOpen}>
            <div className="sd-project-virtual-graph-panel__settings__body">
              <ProjectVirtualGraphPanelProperties
                dataSourceType={dataSource?.type || null}
                setAreResourcePropertiesValid={setAreResourcePropertiesValid}
                setVirtualGraphOptions={setVirtualGraphOptions}
                updateVirtualGraphOptions={updateVirtualGraphOptions}
                virtualGraphOptions={virtualGraphOptions}
              />
              <Divider />
              {editedVirtualGraph ? (
                <ProjectVirtualGraphPanelExistingScopeContainer
                  addItemToQueue={addItemToQueue}
                  dataSource={dataSource}
                  handleRemoveVirtualGraph={handleRemoveVirtualGraph}
                  isPendingTables={isPendingTables}
                  isUsingCustomQuery={isUsingCustomQuery}
                  setIsUsingCustomQuery={setIsUsingCustomQuery}
                  showError={runningQuery.errors.length > 0}
                  tables={tables}
                  virtualGraph={Object.values(virtualGraphsToCreate)[0]}
                />
              ) : (
                <ProjectVirtualGraphPanelNewScopeContainer
                  addItemToQueue={addItemToQueue}
                  dataSource={dataSource}
                  handleRemoveVirtualGraph={handleRemoveVirtualGraph}
                  isPendingTables={isPendingTables}
                  isUsingCustomQuery={isUsingCustomQuery}
                  setIsUsingCustomQuery={setIsUsingCustomQuery}
                  setSelectedTab={setSelectedTab}
                  showError={runningQuery.errors.length > 0}
                  tables={tables}
                  virtualGraphsToCreate={virtualGraphsToCreate}
                />
              )}
            </div>
          </Collapse>
        </div>
        <ProjectVirtualGraphPreview
          enablePreviewRefresh={enablePreviewRefresh}
          existingVirtualGraph={editedVirtualGraph}
          handleUpdateVirtualGraph={handleUpdateVirtualGraph}
          isUsingCustomQuery={isUsingCustomQuery}
          refreshPreview={handleRefreshPreview}
          runningQueryStatus={runningQueryStatus}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          virtualGraphsToCreate={virtualGraphsToCreate}
        />
      </div>
    </div>
  );
};
